import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CountdownTimer } from '@/components/pricing/CountdownTimer';
import {
  addDoc,
  collection,
  doc,
  DocumentReference,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { queryDocuments } from '@/lib/db/core';
import { getUserId, signInAsAnonymous } from '@/lib/auth';

interface Plan {
  name: string;
  price: string | number;
  priceId: string;
  originalPrice: string | number | undefined;
  description: string;
  features: string[];
  popular: boolean;
  buttonText: string;
  stripeButtonId: string;
  recurring: string;
}

const isStringNumber = (str: string | undefined): number | null => {
  const num = Number(str);
  return isNaN(num) ? null : num;
};

function toPlan(p: Product): Plan {
  return {
    name: p.name,
    price: p.price.unit_amount / 100,
    originalPrice: isStringNumber(p.metadata.oldPrice)
      ? p.metadata.oldPrice
      : undefined,
    description: p.description,
    features: Object.entries(p.metadata)
      .map(([key, value]) => (key.startsWith('feat') ? value : undefined))
      .filter(Boolean),
    popular: Boolean(p.metadata.popular),
    buttonText: 'Get Started',
    stripeButtonId: p.id,
    recurring: p.price.recurring.interval,
    priceId: p.price.id,
  };
}

interface Product {
  price: ProductPrice;
  active: boolean;
  description: string;
  id: string;
  images: string[];
  metadata: Metadata;
  name: string;
  popular: boolean;
}

interface Metadata {
  popular?: 0 | 1;
  oldPrice?: string;
  feat1?: string;
  feat2?: string;
  feat3?: string;
  feat4?: string;
  feat5?: string;
}

interface ProductPrice {
  id: string;
  tax_behavior: string;
  interval_count: number;
  billing_scheme: string;
  tiers: null;
  product: string;
  description: null;
  metadata: Metadata;
  type: string;
  trial_period_days: null;
  tiers_mode: null;
  transform_quantity: null;
  active: true;
  recurring: {
    interval: string;
    interval_count: 1;
    aggregate_usage: null;
    usage_type: string;
    meter: null;
    trial_period_days: null;
  };
  unit_amount: 2900;
  interval: string;
  currency: string;
}

async function loadPrices(callback: (v: Product[]) => void) {
  const snapshot = await queryDocuments<Product>('products', [
    {
      field: 'active',
      operator: '==',
      value: true,
    },
  ]);
  const result = await Promise.all(
    snapshot.map(async (doc) => {
      const priceSnap = await getDocs(
        collection(db, 'products', doc.id, 'prices')
      );
      const price = priceSnap.docs
        .slice(0, 1)
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((it) => (it as ProductPrice).active)[0] as ProductPrice;
      console.log('product:', doc);
      console.log('price:', price);
      return {
        ...doc,
        price,
        active: price.active,
      };
    })
  );
  callback(result as Product[]);
}

async function startCheckoutSession(
  price: string,
  onStart: (start: unknown) => void,
  onSuccess: (success: DocumentReference) => void,
  onError: (err: unknown) => void
) {
  onStart('start');
  let userId = getUserId();
  if (!userId) {
    const user = await signInAsAnonymous();
    userId = user.uid;
  }
  addDoc(collection(db, 'users', userId, 'checkout_sessions'), {
    price,
    success_url: window.location.origin,
    cancel_url: window.location.origin,
  }).then(onSuccess, onError);
}

export function PricingPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/buy-button.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    loadPrices((data) => {
      setPlans(() => data.filter((it) => it.active).map((it) => toPlan(it)));
    });
  }, []);

  return (
    <div className="pt-32 pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose the perfect plan for your business. All plans include a
            14-day free trial.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: plan.popular ? 0.2 : 0 }}
              className={`relative rounded-[32px] p-8 ${
                plan.popular
                  ? 'bg-black text-white border-4 border-[#B4FF8C]'
                  : 'bg-white border-2 border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2">
                  <span className="bg-[#B4FF8C] text-black px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                  <CountdownTimer />
                </div>
              )}

              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p
                  className={`text-sm mb-4 ${
                    plan.popular ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">${plan.price}</span>
                  {plan.originalPrice && (
                    <span className="text-xl line-through text-gray-400">
                      ${plan.originalPrice}
                    </span>
                  )}
                  <span
                    className={plan.popular ? 'text-gray-300' : 'text-gray-600'}
                  >
                    / {plan.recurring}
                  </span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check
                      className={`w-5 h-5 mt-0.5 ${
                        plan.popular ? 'text-[#B4FF8C]' : 'text-green-500'
                      }`}
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="w-full h-[42px] flex items-center justify-center">
                <button
                  onClick={() => {
                    startCheckoutSession(
                      plan.priceId,
                      () => {
                        console.log('start');
                      },
                      (docRef) => {
                        console.log(docRef);
                        onSnapshot(docRef, (snap) => {
                          const { error, url } = snap.data() as any;
                          if (error) {
                            // Show an error to your customer and
                            // inspect your Cloud Function logs in the Firebase console.
                            alert(`An error occured: ${error.message}`);
                          }
                          if (url) {
                            // We have a Stripe Checkout URL, let's redirect.
                            window.location.assign(url);
                          }
                        });
                      },
                      (errorArg) => {
                        console.log(errorArg);
                      }
                    );
                  }}
                >
                  Subscribe
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-600 mb-4">
            Need a custom plan for your enterprise?
          </p>
          <Button variant="link" className="text-lg">
            Contact our sales team
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
